import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Lock, Eye, Clock, AlertTriangle, Users, X, History, UserCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CRMRecordPanel = ({ recordType, recordId, onClose }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lockStatus, setLockStatus] = useState(null);

  useEffect(() => {
    if (recordId) loadRecordDetail();
  }, [recordId]);

  const loadRecordDetail = async () => {
    setLoading(true);
    try {
      const urlMap = { client: 'clients', lead: 'leads', deal: 'opportunities', opportunity: 'opportunities' };
      const endpoint = urlMap[recordType] || recordType + 's';
      const [recRes, lockRes] = await Promise.all([
        axios.get(`${API}/${endpoint}/${recordId}`),
        axios.get(`${API}/records/${recordType}/${recordId}/lock`).catch(() => ({ data: { is_locked: false } }))
      ]);
      setRecord(recRes.data);
      setLockStatus(lockRes.data);
    } catch (err) {
      console.error('Failed to load record:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLock = async () => {
    try {
      await axios.post(`${API}/records/${recordType}/${recordId}/lock?lock_type=soft&reason=Manual lock`);
      toast.success('Record locked');
      loadRecordDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock');
    }
  };

  const handleUnlock = async () => {
    try {
      await axios.delete(`${API}/records/${recordType}/${recordId}/lock`);
      toast.success('Record unlocked');
      loadRecordDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    }
  };

  if (loading) return <Card className="animate-pulse"><CardContent className="p-6 h-40" /></Card>;
  if (!record) return null;

  const hasContactWarning = record._contact_warning;
  const isLocked = lockStatus?.is_locked || record._is_locked;
  const isMasked = record._is_masked;
  const recentAccess = record._recent_access || [];

  return (
    <Card className="border-l-4 border-l-black" data-testid="crm-record-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" /> Record Intelligence
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0"><X className="h-4 w-4" /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Warning Banners */}
        {hasContactWarning && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="contact-warning-banner">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Contact Cooldown Active</p>
              <p className="text-xs text-amber-700">{record._contact_warning}</p>
            </div>
          </div>
        )}

        {isMasked && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="masked-data-banner">
            <Shield className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Restricted Data</p>
              <p className="text-xs text-red-700">Some fields are masked due to access restrictions</p>
            </div>
          </div>
        )}

        {/* Lock Status */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg" data-testid="lock-status-section">
          <div className="flex items-center gap-2">
            <Lock className={`h-4 w-4 ${isLocked ? 'text-red-600' : 'text-muted-foreground'}`} />
            <div>
              <p className="text-sm font-medium">{isLocked ? 'Record Locked' : 'Record Unlocked'}</p>
              {isLocked && <p className="text-xs text-muted-foreground">By {lockStatus?.lock?.locked_by_user_name || record._locked_by}</p>}
            </div>
          </div>
          {isLocked ? (
            <Button variant="outline" size="sm" onClick={handleUnlock} className="text-xs">Unlock</Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleLock} className="text-xs">Lock</Button>
          )}
        </div>

        {/* Access Level */}
        {record._access_level && (
          <div className="flex items-center gap-2 text-sm">
            <UserCheck className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Access Level:</span>
            <Badge variant="outline" className="text-xs">{record._access_level}</Badge>
          </div>
        )}

        {/* Last Accessed */}
        {record._last_accessed_by && (
          <div className="flex items-center gap-2 text-sm">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Last accessed by</span>
            <span className="font-medium">{record._last_accessed_by}</span>
            {record._last_accessed_at && <span className="text-xs text-muted-foreground">({new Date(record._last_accessed_at).toLocaleString()})</span>}
          </div>
        )}

        {/* Last Contacted */}
        {record._last_contacted_by && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Last contacted by</span>
            <span className="font-medium">{record._last_contacted_by}</span>
            {record._last_contacted_at && <span className="text-xs text-muted-foreground">({new Date(record._last_contacted_at).toLocaleString()})</span>}
          </div>
        )}

        {/* Recently Accessed By Team */}
        {recentAccess.length > 0 && (
          <div data-testid="recent-access-panel">
            <div className="flex items-center gap-2 mb-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">Recently Accessed By Team</p>
            </div>
            <ScrollArea className="max-h-[150px]">
              <div className="space-y-1.5">
                {recentAccess.map((entry, i) => (
                  <div key={i} className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                    <div className="flex items-center gap-2">
                      <Users className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">{entry.user_name || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Badge variant="outline" className="text-[10px] px-1.5">{entry.access_type}</Badge>
                      <span>{new Date(entry.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
